import { useEffect, useState } from "react";

import { useMedplum } from "@medplum/react";

import { NonCustomCategories } from "@/shared/constants/fee-schedule-categories";
import { getCombinedProcedureCodesValueSetUrl } from "@/utils/billing";
import { toast } from "@/utils/notifications";
import { captureExceptionWithoutPhi } from "@/utils/sentry";

import { ensureProceduresPickerValueSet } from "../services/procedures-picker-value-set";

type ProceduresPickerValueSet = {
  /** The CPT search ValueSet URL; empty while there is no practice to scope it to. */
  valueSetUrl: string;
  /** False until the combined ValueSet has been built, so a search cannot race its creation. */
  isReady: boolean;
  /**
   * The codes the practice has turned on in its Fee Table. Undefined when they are unknown: there
   * is no practice, the build is still running, or it failed.
   */
  activeCodes?: ReadonlySet<string>;
  /**
   * Categories whose turned-on codes could not be checked because their codes are missing from the
   * combined ValueSet. Set only alongside `activeCodes`.
   */
  uncheckedCategories?: ReadonlySet<NonCustomCategories>;
};

/**
 * Builds the practice's combined procedure codes ValueSet when the Procedures picker mounts and
 * returns the URL for its CPT search.
 *
 * When the build fails the picker still searches the practice's combined ValueSet URL, which
 * serves whatever an earlier build stored, and a warning tells the user the list may be incomplete.
 * When the build leaves some categories out, a warning names the quick picker sections that list
 * every code because the practice's Fee Table settings for them could not be checked.
 *
 * @param practiceId The practice Organization id, or undefined when none is resolved.
 * @returns The CPT search ValueSet URL, whether it is ready to search, and the practice's
 *   turned-on codes once known.
 */
export function useProceduresPickerValueSet(
  practiceId: string | undefined,
): ProceduresPickerValueSet {
  const medplum = useMedplum();
  const [builtValueSet, setBuiltValueSet] = useState<{
    practiceId: string;
    activeCodes?: ReadonlySet<string>;
    uncheckedCategories?: ReadonlySet<NonCustomCategories>;
  }>();

  useEffect(() => {
    if (!practiceId) return;

    let isCancelled = false;
    ensureProceduresPickerValueSet(medplum, practiceId).then((result) => {
      if (isCancelled) return;

      if (!result.ok) {
        captureExceptionWithoutPhi(
          "Failed to build combined procedure codes ValueSet",
          result.details,
          {
            feature: "combined-procedure-codes",
            operation: "procedures-picker-open",
            practiceId,
          },
        );
        toast({
          type: "warning",
          title: "Procedure search may be incomplete",
          message:
            "The practice's procedure code list could not be updated. Some CPT codes may not appear in search. Reload the page to try again.",
          autoClose: false,
        });
        setBuiltValueSet({ practiceId });
        return;
      }

      const { activeCodes, uncheckedCategories } = result.data;
      if (uncheckedCategories.size > 0) {
        const sectionNames = [...uncheckedCategories].join(", ");
        toast({
          type: "warning",
          title: "Some quick picker sections show every code",
          message: `The practice's Fee Table settings could not be checked for ${sectionNames}, so those sections list every quick picker code, including any turned off. Reload the page to try again.`,
          autoClose: false,
        });
      }
      setBuiltValueSet({ practiceId, activeCodes, uncheckedCategories });
    });

    return () => {
      isCancelled = true;
    };
  }, [medplum, practiceId]);

  if (!practiceId) return { valueSetUrl: "", isReady: true };

  const isReady = builtValueSet?.practiceId === practiceId;
  return {
    valueSetUrl: getCombinedProcedureCodesValueSetUrl(practiceId),
    isReady,
    ...(isReady && builtValueSet.activeCodes
      ? {
          activeCodes: builtValueSet.activeCodes,
          uncheckedCategories: builtValueSet.uncheckedCategories,
        }
      : {}),
  };
}
